import React from "react"
import { Col, Row } from "react-bootstrap"
import { useSelector } from "react-redux"

import { ReservedFields } from "@/onboarding/features/product/models/Product"
import Field from "@/onboarding/features/product/components/shared/Field"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import {
  getProductField,
  hasProductField,
  templatePrimaryDocumentFieldRenders,
} from "@/onboarding/features/product/selectors"
import { Dimensions, formatWithCanonical } from "@/helpers/units"

// A value in the tour's chosen display unit with the canonical equivalent in
// brackets — "4 ft (1.22 m)", "110.2 lb (50 kg)" — or plain canonical when
// that is the choice. Legacy documents without the unit field fall back to
// the old canonical-only rendering.
const UnitField = ({ dimension, unitField, canonicalName, name, label }): JSX.Element => {
  const hasUnit = useSelector(hasProductField(unitField))
  const { value } = useSelector(getProductField(name))
  const { value: unit } = useSelector(getProductField(unitField))

  if (!hasUnit) return <Field name={name} label={`${label} (${canonicalName})`} />

  const numeric = Number(value)

  return (
    <div className="d-flex flex-column gap-1">
      <span>
        <b>{label}</b>
      </span>
      <TextDisplay
        value={
          Number.isNaN(numeric)
            ? value
            : formatWithCanonical(dimension, numeric, unit || Dimensions[dimension].canonical)
        }
      />
    </div>
  )
}

const Content = (): JSX.Element => {
  const hasDrinkingAge = useSelector(templatePrimaryDocumentFieldRenders("minimum_drinking_age"))
  const hasDrivingAge = useSelector(templatePrimaryDocumentFieldRenders("minimum_driving_age"))
  const hasPregnancy = useSelector(templatePrimaryDocumentFieldRenders("pregnancy_suitable"))

  return (
    <div className="d-flex flex-column gap-4">
      <Row>
        <Col>
          <h3 className="h6">Tour Restrictions:</h3>
          <Row className="d-flex gap-4">
            <Col className="d-flex flex-column gap-2">
              <Field name={ReservedFields.MINIMUM_AGE} label="Minimum Age (yrs)" />
              <Field name={ReservedFields.MAXIMUM_AGE} label="Maximum Age (yrs)" />
            </Col>
            <Col className="d-flex flex-column gap-2">
              <UnitField
                dimension="length"
                unitField={ReservedFields.HEIGHT_UNIT}
                canonicalName="meters"
                name={ReservedFields.MINIMUM_HEIGHT}
                label="Minimum Height"
              />
              <UnitField
                dimension="length"
                unitField={ReservedFields.HEIGHT_UNIT}
                canonicalName="meters"
                name={ReservedFields.MAXIMUM_HEIGHT}
                label="Maximum Height"
              />
            </Col>
            <Col className="d-flex flex-column gap-2">
              <UnitField
                dimension="mass"
                unitField={ReservedFields.WEIGHT_UNIT}
                canonicalName="kilograms"
                name={ReservedFields.MINIMUM_WEIGHT}
                label="Minimum Weight"
              />
              <UnitField
                dimension="mass"
                unitField={ReservedFields.WEIGHT_UNIT}
                canonicalName="kilograms"
                name={ReservedFields.MAXIMUM_WEIGHT}
                label="Maximum Weight"
              />
            </Col>
            {(hasDrinkingAge || hasDrivingAge) && (
              <Col className="d-flex flex-column gap-2">
                {hasDrinkingAge && (
                  <Field name={ReservedFields.MINIMUM_DRINKING_AGE} label="Minimum Drinking Age" />
                )}
                {hasDrivingAge && (
                  <Field name={ReservedFields.MINIMUM_DRIVING_AGE} label="Minimum Driving Age" />
                )}
              </Col>
            )}
            <Col className="d-flex flex-column gap-2">
              <Field
                name={ReservedFields.MEDICAL_CONDITION_RESTRICTIONS}
                label="Medical Conditions"
              />
              {hasPregnancy && (
                <Field name={ReservedFields.PREGNANCY_SUITABLE} label="Suitable for Pregnancy" />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <h3 className="h6">This tour provides:</h3>
          <Row className="ps-4">
            <Col className="d-flex flex-column gap-2">
              <Field name={ReservedFields.INSTRUCTORS_PROVIDED} label="Instructors" />
              <Field
                name={ReservedFields.CHANGING_FACILITIES_PROVIDED}
                label="Changing Facilities"
              />
              <Field name={ReservedFields.STORAGE_LOCKERS_PROVIDED} label="Storage Lockers" />
              <Field name={ReservedFields.TOWELS_PROVIDED} label="Towels For Water Activities" />
              <Field
                name={ReservedFields.WAIVER_FORMS_REQUIRED}
                label="Waiver forms to be signed"
              />
            </Col>
          </Row>
        </Col>
        <Col>
          <h3 className="h6">This tour is suitable for people with:</h3>
          <Row className="ps-4">
            <Col className="d-flex flex-column gap-2">
              <Field name={ReservedFields.WHEELCHAIR_SUITABLE} label="Wheelchairs" />
              <Field name={ReservedFields.BLIND_SUITABLE} label="Blindness" />
              <Field name={ReservedFields.DEAF_SUITABLE} label="Deafness" />
              <Field
                name={ReservedFields.WALKING_DIFFICULTY_SUITABLE}
                label="Walking Difficulties"
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <Field
            name={ReservedFields.INCLUDED_EQUIPMENT}
            label="What equipment is included on tour?"
          />
        </Col>
        <Col>
          <Field
            name={ReservedFields.GUEST_GUIDANCE}
            label="What should guests be advised to bring on tour?"
          />
        </Col>
      </Row>
    </div>
  )
}

export default Content
